import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useMemo, useEffect, useContext, useCallback} from 'react';
import {Color, formatString, insertNewlines, parseColor, setAlpha} from '../utils/utils.js';
import {MenuButton} from '../components/menu-button.js';
import {
    Column,
    Container,
    MaterialContext,
    Panel,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    YogaNodeProps,
} from '@wonderlandengine/react-ui';
import {goToMenu} from '../utils/goToMenu.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Texture} from '@wonderlandengine/api';
import {WindowHeader} from '../components/window-header.js';

import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {Localization} from '../../utils/Localization.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import {SliderItem} from '../components/SliderComponent.js';
import {Window} from '../components/window.js';
import {StatNames} from '../../enums/StatNames.js';
import {MalRoot} from '../components/mal-root.js';
import {Header} from '../components/header.js';
import {BooleanName} from '../../enums/BooleanName.js';
import {BooleanStore} from '../../components/boolean-store.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {ColoredImage} from '../components/colored-image.js';
import {goToMainMenu} from '../utils/gotoMainMenu.js';
import {GameFlowController} from '../../components/game-flow-controller.js';
import {RotatableImage} from '../components/rotatable-image.js';
import {RankManager} from '../../components/rank-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {UnlockManager} from '../../components/unlock-manager.js';
import {RNG} from '../../utils/rng.js';
import {useMemoMaterial} from '../utils/MaterialPool.js';
import {UIText} from '../../types/materials.js';
import {Area} from '../../enums/Area.js';
import {LevelSelect} from '../../components/level-select.js';
import {ScoreController} from '../../components/score-controller.js';
import {BoardColor} from '../../components/board-object.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {Sounds} from '../../enums/Sounds.js';

function getBlobColor(color: BoardColor): Color {
    switch (color) {
        case BoardColor.Green:
            return Float32Array.from(ScoreController.instance.greenColor);
        case BoardColor.Red:
            return Float32Array.from(ScoreController.instance.redColor);
        case BoardColor.Blue:
            return Float32Array.from(ScoreController.instance.blueColor);
        case BoardColor.Yellow:
            return Float32Array.from(ScoreController.instance.yellowColor);
        case BoardColor.Purple:
            return Float32Array.from(ScoreController.instance.purpleColor);
        case BoardColor.Orange:
            return Float32Array.from(ScoreController.instance.orangeColor);
        case BoardColor.Pink:
            return Float32Array.from(ScoreController.instance.pinkColor);
        case BoardColor.Turquoise:
            return Float32Array.from(ScoreController.instance.turquoiseColor);
        case BoardColor.Brown:
            return Float32Array.from(ScoreController.instance.brownColor);
        case BoardColor.Moon:
            return Float32Array.from(ScoreController.instance.moonColor);
    }
}

function isStageLocked(booleanName: BooleanName) {
    const unlocked = BooleanStore.instance.get(booleanName);
    return !unlocked;
}

function lockImage(booleanName: BooleanName, image: Texture): Texture {
    if (!isStageLocked(booleanName)) {
        return image;
    } else return UiTexturesManager.instance.buttonLock;
}

type StageSelectItem = {
    title: string;
    description: string;
    // difficultyLevel: number;
    // gamesPlayedStat: StatNames;
    // autoPop: boolean;
    // unlockedItem: BooleanName;
    //lockFlag?: BooleanName;
    ignoreLock: boolean;
    // implemented: boolean;
    blobs: BoardColor[];
};

const StageData = new Map<Area, StageSelectItem>([
    [
        Area.Island,
        {
            title: 'Island',
            description: 'IslandDescription',
            // difficultyLevel: 1,
            // gamesPlayedStat: StatNames.QuickplayGamesPlayed,
            // autoPop: false,
            // unlockedItem: BooleanName.QuickplayModeClicked,
            ignoreLock: true,
            // implemented: true,
            blobs: [BoardColor.Green, BoardColor.Orange, BoardColor.Blue],
        },
    ],
    [
        Area.Candy,
        {
            title: 'Candy',
            description: 'CandylandDescription',
            // difficultyLevel: 1,
            // gamesPlayedStat: StatNames.CupcakeGamesPlayed,
            // autoPop: true,
            // unlockedItem: BooleanName.CupcakeModeClicked,
            // lockFlag: BooleanName.CupcakeModeUnlocked,
            ignoreLock: true,
            // implemented: true,
            blobs: [BoardColor.Red, BoardColor.Orange, BoardColor.Yellow],
        },
    ],
]);

type StageSelectState = {
    title: string;
    description: string;
    // diffLevel: number;
    isImplemented: boolean;
    isLocked: boolean;
    // isAutoPop: boolean;
    selectedArea: Area;
    blobs: BoardColor[];
};

export const StageSelectPanel = (props: React.PropsWithChildren<YogaNodeProps> & {}) => {
    const [isTextReadyToRender, setIsTextReadyToRender] = useState(false);

    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);

    const rank = useMemo(
        () =>
            formatString(
                Localization.instance.get('RankFormat'),
                formatNumber(RankManager.instance.rank)
            ),
        [props]
    );

    const xpPercentage = useMemo(
        () => RankManager.instance.currentRankPercentComplete,
        [props]
    );

    const [modeState, setModeState] = useState<StageSelectState>({
        title: '',
        description: '',
        isImplemented: true,
        isLocked: false,
        selectedArea: Area.Island,
        blobs: [],
    });

    const [selectModeText] = useState(
        Localization.instance.get(
            RNG.getItem(['GameSetupMenuText1', 'GameSetupMenuText2', 'GameSetupMenuText3'])
        )
    );

    const textMaterial = useMemoMaterial('textMaterial', () => {
        return mats.textMaterial.clone() as UIText;
    });

    const descriptionTextMaterial = useMemoMaterial('descriptionTextMaterial_90%', () => {
        const mat = rotbMats.regularTextMaterial.clone() as UIText;
        mat.setColor(parseColor(setAlpha(colorSwatch.DescriptionText, '90%')));
        return mat;
    });

    const onSelectionChanged = useCallback(
        (item: SliderItem) => {
            const currentSelectedArea = Area[item.id];
            const data = StageData.get(currentSelectedArea);
            // const locked = data.ignoreLock ? false : isGameModeLocked(data.lockFlag!);
            // if (
            //     modeState.isLocked !== locked ||
            //     modeState.isImplemented !== data.implemented
            // ) {
            //     setIsTextReadyToRender(false);
            // }
            setModeState({
                title: Localization.instance.get(data.title),
                description: Localization.instance.get(data.description),
                //     diffLevel: data.difficultyLevel,
                isImplemented: true,
                //     isAutoPop: data.autoPop,
                isLocked: false,
                selectedArea: currentSelectedArea,
                blobs: data.blobs,
            });
        },
        [modeState.isLocked, modeState.isImplemented]
    );

    useEffect(() => {
        if (!isTextReadyToRender) {
            let frameId = setTimeout(() => {
                setIsTextReadyToRender(true);
            }, 0);
            return () => clearTimeout(frameId);
        }
    }, [isTextReadyToRender]);

    const handleClick = useCallback(() => {
        //GameFlowController.instance.setMode(modeState.selectedMode);
        LevelSelect.instance.changeLevel(modeState.selectedArea);
        goToMenu(MenuPanels.Loadout);
    }, [modeState.selectedArea]);

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={Localization.instance.get('AreaSelect')} />
                        <Container marginLeft={-20} marginBottom={40}>
                            <MalRoot
                                rank={rank}
                                xpPercentage={xpPercentage}
                                additionalRotation={[-1.5, 1.5, 0]}
                                text={selectModeText}
                            ></MalRoot>
                        </Container>
                        <Container flexGrow={1}>
                            <Container>
                                <Carousel
                                    loop={false}
                                    onSelectionChanged={onSelectionChanged}
                                >
                                    <CarouselItem
                                        title="Island"
                                        id={'Island'}
                                        image={UiTexturesManager.instance.stageIsland}
                                    ></CarouselItem>
                                    <CarouselItem
                                        title="Candyland"
                                        id="Candy"
                                        image={UiTexturesManager.instance.stageCandy}
                                    ></CarouselItem>
                                </Carousel>
                            </Container>
                            <Header
                                title={modeState.title}
                                color={colorSwatch.SubTitleHighBackground}
                                center={true}
                            />
                            <Row margin={20} alignSelf={Align.Center} gap={10}>
                                {modeState.blobs.map((blobColor, index) => {
                                    return (
                                        <Panel key={index} rounding={5}>
                                            <ColoredImage
                                                src={UiTexturesManager.instance.blob}
                                                width={40}
                                                height={40}
                                                rounding={0}
                                                color={getBlobColor(blobColor)}
                                            ></ColoredImage>
                                        </Panel>
                                    );
                                })}
                            </Row>
                            <Container
                                height={75}
                                alignContent={Align.FlexStart}
                                padding={10}
                            >
                                <Text
                                    material={descriptionTextMaterial}
                                    fontSize={24}
                                    textAlign="center"
                                >
                                    {insertNewlines(modeState.description, 44)}
                                </Text>
                            </Container>
                        </Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.SpaceAround}>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.CancelButton}
                        fontSize={28}
                        icon={UiTexturesManager.instance.home}
                        iconWidth={38}
                        iconHeight={40}
                        sound={Sounds.cancel}
                        onClick={() => goToPreviousMenu(MenuPanels.StageSelect)}
                    >
                        {Localization.instance.get('LoseProgressDialogReject')}
                    </MenuButton>
                    {isTextReadyToRender ? (
                        modeState.isImplemented && !modeState.isLocked ? (
                            <MenuButton
                                width={250}
                                height={54}
                                margin={6}
                                color={colorSwatch.AcceptButton}
                                fontSize={32}
                                iconWidth={38}
                                iconHeight={40}
                                onClick={handleClick}
                                icon={UiTexturesManager.instance.arrow}
                            >
                                {Localization.instance.get('Next')}
                            </MenuButton>
                        ) : (
                            <Text
                                width={250}
                                height={54}
                                top={18}
                                margin={6}
                                fontSize={24}
                                textAlign="center"
                                material={textMaterial}
                                text={modeState.isImplemented ? 'Locked' : 'Coming Soon'}
                            ></Text>
                        )
                    ) : null}
                </Row>
            </Column>
        </Container>
    );
};
